import React, { useEffect, useState } from 'react';
import { Mail, Newspaper, Target, Megaphone, Sparkles, ExternalLink } from 'lucide-react';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { useOs2, useRangeLabel, PageHead, Card, Stat, Th, Td, SourceNote, LoadingState, EmptyList } from './os2ui';
import GoogleGrowthPanel from '../../os3/homes/GoogleGrowthPanel';

/* Marketing Intelligence — "Which channels bring qualified traffic, and which waste money?" */

function RachelCampaigns({ isEs }) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [items, setItems] = useState([]);
  const load = () => authFetch('/os2/rachel-campaigns').then((r) => r.json()).then((j) => setItems(j.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (prompt.trim().length < 8 || busy) return;
    setBusy(true); setResult(null);
    try {
      const r = await authFetch('/os2/rachel-campaign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim() }) });
      const j = await r.json();
      setResult(j);
      if (j.ok) { setPrompt(''); load(); }
    } catch {
      setResult({ ok: false, reason: 'network' });
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 mb-6" data-testid="rachel-campaigns">
      <h2 className="text-[15px] font-semibold tracking-tight mb-1 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Campañas con Rachel' : 'Campaigns with Rachel'}
      </h2>
      <p className="text-[12.5px] text-gray-500 mb-3">
        {isEs
          ? 'Pídele una campaña en lenguaje natural: Rachel crea el segmento, la lista en Brevo y deja el borrador listo para tu revisión.'
          : 'Ask for a campaign in natural language: Rachel builds the segment, the Brevo list, and leaves the draft ready for your review.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="rachel-campaign-prompt"
          placeholder={isEs
            ? 'Ej: campaña en español para quienes vieron la demo pero no pidieron contacto, invitándolos a 15 minutos'
            : 'E.g.: Spanish campaign for people who saw the demo but never asked for contact, inviting them to 15 minutes'}
          className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 text-[13px] outline-none focus:border-[#F77F11] transition-colors resize-none" />
        <button onClick={run} disabled={busy || prompt.trim().length < 8} data-testid="rachel-campaign-run"
          className="shrink-0 h-11 sm:h-auto px-5 rounded-2xl bg-[#F77F11] text-white text-[13px] font-bold hover:brightness-110 transition-all disabled:opacity-40">
          {busy ? (isEs ? 'Creando…' : 'Creating…') : (isEs ? 'Crear borrador' : 'Create draft')}
        </button>
      </div>
      {result && (
        <div className={`mt-3 rounded-2xl border px-4 py-3 text-[12.5px] ${result.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
          data-testid="rachel-campaign-result">
          {result.ok ? (
            <>
              <p className="font-bold">✓ {isEs ? 'Borrador creado en Brevo' : 'Draft created in Brevo'} — “{result.plan?.name}”</p>
              <p className="mt-0.5">{isEs ? 'Segmento' : 'Segment'}: {result.plan?.audience} · {result.segment_size} {isEs ? 'contactos' : 'contacts'} ({result.contacts_added} {isEs ? 'añadidos a la lista' : 'added to the list'}) · {isEs ? 'Asunto' : 'Subject'}: “{result.plan?.subject}”</p>
              <a href="https://app.brevo.com/marketing-dashboard" target="_blank" rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 font-bold text-emerald-700 hover:underline">
                {isEs ? 'Revisar y enviar desde Brevo' : 'Review & send from Brevo'} <ExternalLink className="w-3 h-3" />
              </a>
            </>
          ) : (
            <p className="font-semibold">{isEs ? 'No se pudo crear' : 'Could not create'} — {result.reason === 'empty_segment' ? (isEs ? 'el segmento quedó vacío, prueba otra audiencia' : 'the segment came back empty, try another audience') : result.reason}</p>
          )}
        </div>
      )}
      {!!items.length && (
        <div className="mt-4 border-t border-gray-100 pt-3" data-testid="rachel-campaigns-history">
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400 mb-1.5">{isEs ? 'Últimas campañas' : 'Recent campaigns'}</p>
          {items.slice(0, 5).map((c) => (
            <p key={c.id} className="flex items-center gap-2 py-1 text-[12px] text-gray-600">
              <span className={`w-1.5 h-1.5 rounded-full ${c.status === 'draft' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
              <span className="font-semibold truncate">{c.plan?.name}</span>
              <span className="text-gray-400 shrink-0">· {c.segment_size} {isEs ? 'contactos' : 'contacts'} · {new Date(c.at).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short' })}</span>
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function MarketingOsPage({ embedded = false }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/marketing');
  const { label } = useRangeLabel(isEs);
  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const t = d.traffic; const e = d.email;
  const conv = d.conversions || {};
  const attr = d.attribution || {};

  return (
    <div data-testid="os2-marketing">
      <PageHead
        title={isEs ? 'Inteligencia de Marketing' : 'Marketing Intelligence'}
        question={isEs ? '¿Qué canales traen tráfico calificado — y cuáles desperdician dinero?' : 'Which channels bring qualified traffic — and which waste money?'}
        fresh={embedded ? null : { at: fetchedAt, onReload: reload, loading, isEs }}
        module={embedded ? undefined : 'Analytics'}
      />

      <RachelCampaigns isEs={isEs} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={isEs ? 'Páginas vistas' : 'Page views'} value={t.views} testid="mkt-views" accent help="page_views" isEs={isEs} scope="period" source="Kitxens beacon" />
        <Stat label={isEs ? 'Clics salientes' : 'Outbound clicks'} value={t.clicks_out} testid="mkt-clicks" isEs={isEs} scope="period" source="Kitxens beacon" />
        <Stat label={isEs ? 'Tasa de apertura email' : 'Email open rate'} value={e.open_rate != null ? `${e.open_rate}%` : '—'} sub={isEs ? 'ventana fija 30d de Brevo' : 'Brevo fixed 30d window'} testid="mkt-open-rate" help="open_rate" isEs={isEs} source="Brevo" />
        <Stat label={isEs ? 'Tasa de clic email' : 'Email click rate'} value={e.click_rate != null ? `${e.click_rate}%` : '—'} sub={e.transactional_30d ? `${e.transactional_30d.delivered} ${isEs ? 'entregados · ventana fija 30d' : 'delivered · fixed 30d window'}` : (isEs ? 'ventana fija 30d de Brevo' : 'Brevo fixed 30d window')} testid="mkt-click-rate" help="click_rate" isEs={isEs} source="Brevo" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6" data-testid="mkt-top-pages">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3">{isEs ? `Top landing pages · ${label}` : `Top landing pages · ${label}`}</h2>
          {(t.top_pages || []).length ? t.top_pages.map((p) => (
            <div key={p.path} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
              <span className="text-[13px] text-gray-700 truncate">{p.path}</span>
              <span className="text-[13px] font-semibold tabular-nums">{p.views}</span>
            </div>
          )) : <EmptyList isEs={isEs} />}
          <SourceNote source="Kitxens beacon" isEs={isEs} className="mt-3" />
        </Card>
        <Card className="p-6" data-testid="mkt-sources">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3">{isEs ? `Fuentes de tráfico · ${label}` : `Traffic sources · ${label}`}</h2>
          {(t.sources || []).length ? t.sources.map((s) => (
            <div key={s.ref} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
              <span className="text-[13px] text-gray-700 capitalize">{s.ref}</span>
              <span className="text-[13px] font-semibold tabular-nums">{s.visits}</span>
            </div>
          )) : <EmptyList isEs={isEs} />}
          <SourceNote source={isEs ? 'Kitxens beacon first-party (/api/track)' : 'Kitxens first-party beacon (/api/track)'} isEs={isEs} className="mt-3" />
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6" data-testid="mkt-conversions">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-[#F77F11]" /> {isEs ? `Conversiones (leads) · ${label}` : `Conversions (leads) · ${label}`}</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { k: 'free_audit_start', es: 'Auditorías iniciadas', en: 'Audits started' },
              { k: 'contact_whatsapp', es: 'Clics WhatsApp', en: 'WhatsApp clicks' },
              { k: 'contact_phone', es: 'Clics teléfono', en: 'Phone clicks' },
              { k: 'contact_email', es: 'Clics email', en: 'Email clicks' },
            ].map((c) => (
              <div key={c.k} className="rounded-xl border border-gray-100 p-3" data-testid={`mkt-conv-${c.k}`}>
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">{isEs ? c.es : c.en}</p>
                <p className="text-[22px] font-bold tabular-nums mt-0.5">{conv[c.k] ?? 0}</p>
              </div>
            ))}
          </div>
          {(conv.top_whatsapp_pages || []).length > 0 && (
            <>
              <p className="mt-3 text-[11px] tracking-wider uppercase text-gray-400 font-medium">{isEs ? 'Páginas que generan WhatsApp' : 'Pages driving WhatsApp'}</p>
              {conv.top_whatsapp_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                  <span className="text-[12.5px] text-gray-600 truncate">{p.path}</span>
                  <span className="text-[12.5px] font-semibold tabular-nums">{p.clicks}</span>
                </div>
              ))}
            </>
          )}
          <SourceNote source="Kitxens beacon" isEs={isEs} className="mt-3" />
          <p className="mt-1 text-[11px] text-gray-400">{isEs ? 'Los mismos eventos llegan a GA4 como free_audit_start / contact_whatsapp — márcalos como Eventos Clave en GA4 Admin.' : 'Same events flow into GA4 as free_audit_start / contact_whatsapp — mark them as Key Events in GA4 Admin.'}</p>
        </Card>
        <Card className="p-6" data-testid="mkt-attribution">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#F77F11]" /> {isEs ? `Campañas UTM · ${label}` : `UTM campaigns · ${label}`}</h2>
          {(attr.utm_sources || []).length ? (
            <>
              <p className="text-[11px] tracking-wider uppercase text-gray-400 font-medium mb-1">{isEs ? 'Por fuente (utm_source)' : 'By source (utm_source)'}</p>
              {attr.utm_sources.map((s) => (
                <div key={s.source} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                  <span className="text-[13px] text-gray-700 capitalize">{s.source}</span>
                  <span className="text-[13px] font-semibold tabular-nums">{s.visits}</span>
                </div>
              ))}
              {(attr.utm_campaigns || []).length > 0 && (
                <>
                  <p className="mt-3 text-[11px] tracking-wider uppercase text-gray-400 font-medium mb-1">{isEs ? 'Por campaña (utm_campaign)' : 'By campaign (utm_campaign)'}</p>
                  {attr.utm_campaigns.map((c) => (
                    <div key={c.campaign} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                      <span className="text-[13px] text-gray-700">{c.campaign}</span>
                      <span className="text-[13px] font-semibold tabular-nums">{c.visits}</span>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : (
            <p className="text-[13px] text-gray-500">
              {isEs
                ? 'Aún sin visitas con UTM. Etiqueta tus posts y emails así: kitxens.com/?utm_source=instagram&utm_campaign=lanzamiento — y aquí verás exactamente qué trajo cada publicación.'
                : 'No UTM-tagged visits yet. Tag your posts & emails like: kitxens.com/?utm_source=instagram&utm_campaign=launch — and this panel will show exactly what each post brought.'}
            </p>
          )}
          <SourceNote source={isEs ? 'Kitxens beacon first-party (/api/track)' : 'Kitxens first-party beacon (/api/track)'} isEs={isEs} className="mt-3" />
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6" data-testid="mkt-campaigns">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Campañas Brevo (reales)' : 'Brevo campaigns (real)'}
            <a href="https://app.brevo.com/marketing-campaign/list" target="_blank" rel="noreferrer" className="ml-auto text-[11.5px] font-semibold text-[#F77F11] hover:underline" data-testid="mkt-open-brevo">
              {isEs ? 'Abrir Brevo ↗' : 'Open Brevo ↗'}
            </a>
          </h2>
          {(e.campaigns || []).length ? (
            <table className="w-full">
              <thead><tr><Th>{isEs ? 'Campaña' : 'Campaign'}</Th><Th>{isEs ? 'Enviados' : 'Sent'}</Th><Th>{isEs ? 'Aperturas' : 'Opens'}</Th><Th>Clicks</Th></tr></thead>
              <tbody>
                {e.campaigns.map((c) => (
                  <tr key={c.id}>
                    <Td className="max-w-[220px]">
                      <a href={`https://app.brevo.com/camp/report/id/${c.id}`} target="_blank" rel="noreferrer"
                        className="truncate block hover:text-[#F77F11] hover:underline transition-colors" title={isEs ? 'Abrir reporte en Brevo' : 'Open report in Brevo'}
                        data-testid={`mkt-campaign-link-${c.id}`}>{c.name} ↗</a>
                    </Td>
                    <Td className="tabular-nums">{c.sent}</Td>
                    <Td className="tabular-nums">{c.opens}</Td>
                    <Td className="tabular-nums">{c.clicks}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyList isEs={isEs} text={isEs ? 'Sin campañas todavía' : 'No campaigns yet'} />}
          {d.newsletter?.available !== false && d.newsletter?.last_run && (
            <p className="mt-3 text-[12px] text-gray-500">Newsletter: {d.newsletter.last_run.status} · #{d.newsletter.last_run.campaign_id}</p>
          )}
          <SourceNote source="Brevo" isEs={isEs} className="mt-3" />
        </Card>
        <Card className="p-6" data-testid="mkt-articles">
          <h2 className="text-[15px] font-semibold tracking-tight mb-3 flex items-center gap-2"><Newspaper className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Contenido que convierte' : 'Content that converts'}</h2>
          <p className="text-[12px] text-gray-400 mb-2">
            {d.content.articles_published} {isEs ? 'artículos publicados (histórico)' : 'published articles (lifetime)'} · +{d.content.subscribers_new} {isEs ? `suscriptores · ${label}` : `subscribers · ${label}`}
          </p>
          {(d.content.top_articles || []).length ? d.content.top_articles.map((a) => (
            <div key={a.url} className="flex items-center justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
              <span className="text-[13px] text-gray-700 truncate">{a.title}</span>
              <span className="text-[12px] font-semibold tabular-nums text-gray-500">{a.clicks || 0} clicks</span>
            </div>
          )) : <EmptyList isEs={isEs} text={isEs ? 'Sin clics en artículos aún' : 'No article clicks yet'} />}
          {(t.top_reads || []).length > 0 && (
            <>
              <p className="mt-3 text-[11px] tracking-wider uppercase text-gray-400 font-medium">{isEs ? 'Más leídos (tiempo real de lectura)' : 'Most read (real read time)'}</p>
              {t.top_reads.map((r) => (
                <p key={r.slug} className="text-[12.5px] text-gray-500 py-0.5 truncate">· {r.slug} — {r.reads} {isEs ? 'lecturas' : 'reads'} · {r.avg_seconds}s</p>
              ))}
            </>
          )}
          <SourceNote source="Kitxens DB" isEs={isEs} className="mt-3" />
        </Card>
      </div>

      <GoogleGrowthPanel isEs={isEs} />
    </div>
  );
}
